import React, { useState, useEffect } from 'react';
import { Card, Button, Row, Col, Typography, Badge, Modal } from 'antd';
import { ShoppingCartOutlined, ArrowLeftOutlined, HeartOutlined, SafetyOutlined, BulbOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import StripeCheckout from '../../components/checkout/StripeCheckout';
import { trackProductView, trackAddToCart, trackPurchase } from '../../../utils/analytics';
import unicornImage from '../../../assets/images/Classic Unicorn.png';

const { Title, Text, Paragraph } = Typography;

const CalmiUnicorn = () => {
  const history = useHistory();
  const [cartItems, setCartItems] = useState([]);
  const [checkoutVisible, setCheckoutVisible] = useState(false);

  useEffect(() => {
    trackProductView({
      id: 'lumi-unicorn',
      name: 'Lumi Unicorn',
      price: 499,
      collection: 'Classic Bears'
    });
  }, []);

  const product = {
    id: 'lumi-unicorn',
    name: 'Lumi Unicorn',
    price: 499,
    image: unicornImage,
    collection: 'Classic Bears',
    subtitle: 'Mystical imagination companion',
    description: 'A mystical unicorn companion in pristine white plush with a delicate spiral horn and soft pink inner ears. Features flowing mane details and sparks imagination while providing gentle comfort.',
    fullDescription: 'Enter a world of magic and wonder with the Lumi Unicorn. This enchanting companion features pristine white plush with delicate pastel accents, a graceful spiral horn, and a flowing mane that captures the imagination. The ethereal white chest light adds to the magical experience, creating a soothing, otherworldly presence. Perfect for children who love fantasy and dreams, the Lumi Unicorn encourages creativity, imagination, and positive thinking while providing the same advanced AI wellness support as all Lumi companions.',
    specs: { 
      dimensions: '12" x 8" x 6"', 
      color: 'Cream White', 
      texture: 'Magical Plush',
      batteryLife: '8+ hours',
      charging: 'USB-C',
      ageRange: '4-12 years'
    },
    features: [
      'Mystical unicorn design with delicate spiral horn',
      'Pristine white plush with soft pink accent details',
      'Flowing mane details that enhance the magical feel',
      'Ethereal white chest light for magical ambiance',
      'AI conversations focused on creativity and imagination',
      'Perfect for bedtime stories and dream encouragement'
    ],
    idealFor: [
      'Children who love fantasy and magic',
      'Encouraging imagination and creative expression',
      'Bedtime comfort and magical story time',
      'Building confidence through positive self-image',
      'Supporting sensitive children with gentle encouragement',
      'Creating magical daily routines'
    ]
  };

  const addToCart = () => {
    trackAddToCart(product, 1);
    setCartItems([{ ...product, quantity: 1 }]);
    setCheckoutVisible(true);
  };

  const handlePaymentSuccess = (paymentIntent) => {
    trackPurchase({
      transactionId: paymentIntent.id,
      total: product.price,
      items: [{ ...product, quantity: 1 }]
    });
    setCheckoutVisible(false);
    setCartItems([]);
    Modal.success({
      title: 'Preorder Confirmed! 🎉',
      content: (
        <div>
          <p>Your {product.name} preorder has been confirmed! It's guaranteed to arrive in summer 2027.</p>
          <p>You'll receive an email confirmation shortly.</p>
          <p><strong>Order ID:</strong> {paymentIntent.id}</p>
        </div>
      ),
    });
  };

  const handlePaymentError = (error) => {
    Modal.error({
      title: 'Payment Failed',
      content: 'There was an issue processing your payment. Please try again.',
    });
  };

  return (
    <div style={{ minHeight: '100vh', background: '#fff' }}>
      {/* Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #e6e6fa 0%, #dda0dd 100%)',
        padding: '20px 0',
        marginBottom: '40px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />} 
            onClick={() => history.push('/')}
            style={{ color: 'white', marginBottom: '10px' }}
          >
            Back to Lumi Bears
          </Button>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ fontSize: '32px', marginRight: '15px' }}>🦄</span>
            <div>
              <Title level={2} style={{ color: 'white', margin: 0 }}>
                {product.name}
              </Title>
              <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: '18px' }}>
                {product.subtitle}
              </Text>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
        {/* Product Overview */}
        <Row gutter={[48, 32]} style={{ marginBottom: '60px' }}>
          <Col xs={24} lg={12}>
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              backgroundColor: '#f5f5f5', 
              borderRadius: '12px', 
              height: '500px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              border: '1px solid #e0e0e0'
            }}>
              <img 
                src={product.image} 
                alt={product.name}
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '100%', 
                  objectFit: 'contain'
                }}
              />
            </div>
          </Col>
          <Col xs={24} lg={12}>
            <Badge.Ribbon text={product.collection} color="purple">
              <Card>
                <Title level={1} style={{ marginTop: 0 }}>
                  {product.name}
                </Title>
                <Title level={2} style={{ color: '#e74c3c', marginBottom: '20px' }}>
                  ${product.price} <Text style={{ fontSize: '16px', color: '#7f8c8d' }}>Preorder · Guaranteed delivery in summer 2027</Text>
                </Title>
                <Paragraph style={{ fontSize: '18px', marginBottom: '30px' }}>
                  {product.fullDescription}
                </Paragraph>
                
                <Button
                  type="primary"
                  size="large"
                  icon={<ShoppingCartOutlined />}
                  onClick={addToCart}
                  block
                  style={{
                    background: '#e74c3c',
                    borderColor: '#e74c3c',
                    height: '60px',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    marginBottom: '20px'
                  }}
                >
                  Preorder Now - ${product.price}
                </Button>

                <Card size="small" style={{ backgroundColor: '#f8f8ff' }}>
                  <Text strong>📅 Preorder Details:</Text>
                  <ul style={{ marginTop: '8px', marginBottom: 0 }}>
                    <li>✨ Preordering guarantees your Lumi in summer 2027</li>
                    <li>💳 Pay today to reserve yours</li>
                    <li>🔄 Cancel anytime before shipping</li>
                  </ul>
                </Card>
              </Card>
            </Badge.Ribbon>
          </Col>
        </Row>

        {/* Specifications & Features */}
        <Card title="Technical Specifications" style={{ marginBottom: '40px' }}>
          <Row gutter={[24, 16]}>
            {Object.entries(product.specs).map(([key, value]) => (
              <Col xs={12} md={6} key={key}>
                <Text strong>{key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}:</Text><br/>
                <Text>{value}</Text>
              </Col>
            ))}
          </Row>
        </Card>

        <Row gutter={[32, 32]} style={{ marginBottom: '60px' }}>
          <Col xs={24} md={12}>
            <Card title="Key Features" style={{ height: '100%' }}>
              <ul>
                {product.features.map((feature, index) => (
                  <li key={index} style={{ marginBottom: '8px', fontSize: '16px' }}>
                    {feature}
                  </li>
                ))}
              </ul>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card title="Ideal For" style={{ height: '100%' }}>
              <ul>
                {product.idealFor.map((use, index) => (
                  <li key={index} style={{ marginBottom: '8px', fontSize: '16px' }}>
                    {use}
                  </li>
                ))}
              </ul>
            </Card>
          </Col>
        </Row>

        <Title level={2} style={{ textAlign: 'center', marginBottom: '40px' }}>
          Why Choose the Lumi Unicorn?
        </Title>
        <Row gutter={[32, 32]} style={{ marginBottom: '60px' }}>
          <Col xs={24} md={8}>
            <Card style={{ textAlign: 'center', height: '100%' }}>
              <HeartOutlined style={{ fontSize: '48px', color: '#e6e6fa', marginBottom: '16px' }} />
              <Title level={4}>Magical Inspiration</Title>
              <Text>
                Sparks imagination and creativity while building confidence through the power of magical thinking and positive dreams.
              </Text>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card style={{ textAlign: 'center', height: '100%' }}>
              <SafetyOutlined style={{ fontSize: '48px', color: '#dda0dd', marginBottom: '16px' }} />
              <Title level={4}>Gentle Encouragement</Title>
              <Text>
                Perfect for sensitive children, providing gentle support and encouragement through magical conversations and stories.
              </Text>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card style={{ textAlign: 'center', height: '100%' }}>
              <BulbOutlined style={{ fontSize: '48px', color: '#da70d6', marginBottom: '16px' }} />
              <Title level={4}>Dream Builder</Title>
              <Text>
                Encourages positive thinking, big dreams, and creative problem-solving through magical conversations and bedtime stories.
              </Text>
            </Card>
          </Col>
        </Row>
      </div>

      <Modal
        title=""
        open={checkoutVisible}
        onCancel={() => setCheckoutVisible(false)}
        footer={null}
        width={900}
        style={{ top: 20 }}
        destroyOnClose
      >
        <StripeCheckout
          cartItems={cartItems}
          onPaymentSuccess={handlePaymentSuccess}
          onPaymentError={handlePaymentError}
        />
      </Modal>
    </div>
  );
};

export default CalmiUnicorn;